<template>
    <v-container fluid>
        <v-carousel
            v-if="dashboardSections.length > 0"
            v-model="carouselIndex"
            height="auto"
            hide-delimiters
            :show-arrows="false"
            :continuous="false"
            :dark="$vuetify.theme.dark"
            :light="!$vuetify.theme.dark"
            :touchless="true"
        >
            <v-carousel-item v-for="(section) in dashboardSections" :key="section.id">
                <v-container v-if="section.id == 1" fluid>
                    <v-row v-if="hasMachinesDashboard" dense align="center">
                        <v-col cols="12" class="pt-2">
                            <v-container fluid class="pa-0">
                                <v-row dense justify="end">
                                    <v-col cols="auto">
                                        <v-icon color="secondary" x-large @click="getMachinesReviewDashboard()">
                                            mdi-chevron-right-circle
                                        </v-icon>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col v-if="hasDashboardItems" cols="12">
                            <v-container fluid class="pa-0">
                                <v-row>
                                    <component
                                        :is="widget.type"
                                        v-for="widget in dashboardItems"
                                        :key="widget.id"
                                        :widget="widget"
                                        :permissions="userPermissions"
                                    />
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col v-else cols="12">
                            <v-alert type="info" text border="left">
                                Brak widgetów.
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-container>
                <v-container v-if="section.id == 2" fluid>
                    <MachinesReview @go-backwards="carouselIndex--" />
                </v-container>
            </v-carousel-item>
        </v-carousel>
        <v-container v-else fluid>
            <v-row>
                <v-col cols="12">
                    <v-alert type="info" text border="left">
                        Brak widgetów.
                    </v-alert>
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script>
import store from '@/store'
import PageTitle from '@/components/common/PageTitle'
import SimpleCount from './Widgets/SimpleCount'
import ThreeMonthsTotalReport from './Widgets/ThreeMonthsTotalReport'
import ThreeMonthsReport from './Widgets/ThreeMonthsReport'
import OrdersDelayedReport from './Widgets/OrdersDelayedReport'
import OrdersProcesses from './Widgets/OrdersProcesses'
import ProcessedOrders from './Widgets/ProcessedOrders'
import NewOrders from './Widgets/NewOrders'
import OrdersPerDayNotSentReport from './Widgets/OrdersPerDayNotSentReport'
import PaperNeeded from './Widgets/PaperNeeded'
import UserTasksList from './Widgets/UserTasksList'
import FourMonthsComplaintsReport from './Widgets/FourMonthsComplaintsReport'
import MachinesReview from './Widgets/MachinesReview'
import AccidentsDividedPerDepartment from './Widgets/AccidentsDividedPerDepartment'
import QualitativeNotes from './Widgets/QualitativeNotes'
import NumberOfOrdersPerImposition from './Widgets/NumberOfOrdersPerImposition'
import StopCardsCount from './Widgets/StopCardsCount'
import OrdersSentDailyByShipperReport from './Widgets/OrdersSentDailyByShipperReport'
import { format } from 'date-fns'
import { errorHandler } from '@/utils/errorHandler'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'

export default {
    name: 'Dashboard',
    components: {
        PageTitle,
        SimpleCount,
        OrdersDelayedReport,
        OrdersProcesses,
        ProcessedOrders,
        NewOrders,
        OrdersPerDayNotSentReport,
        ThreeMonthsTotalReport,
        ThreeMonthsReport,
        PaperNeeded,
        UserTasksList,
        FourMonthsComplaintsReport,
        MachinesReview,
        AccidentsDividedPerDepartment,
        QualitativeNotes,
        NumberOfOrdersPerImposition,
        StopCardsCount,
        OrdersSentDailyByShipperReport
    },
    mixins: [
        filters,
        pagination
    ],
    beforeRouteEnter(to, from, next) {
        if (from.name === 'reportsReportsShiftSummariesDetailedReports') {
            next(vm => {
                vm.fromReportsReportsShiftSummariesDetailedReports = true
                vm.carouselIndex = 1
            })
        } else if (from.name === 'userLogin') {
            next()
        } else {
            store.dispatch('dashboard/getDashboardSections')
                .catch(error => errorHandler(error))

            store.dispatch('administration/getProductionDepartments', { query: '?hasMachines=true&showAtDashboard=true' })
                .catch(error => errorHandler(error))

            store.dispatch('dashboard/clearDashboardItems')

            store.dispatch('dashboard/getDashboardItems')
                .then((response) => {
                    let promises = []

                    response.data.results.forEach(item => {
                        if (item.codeName === 'machineReview') {
                            store.dispatch('dashboard/setMachinesDashboard', item)
                        } else if (item.codeName === 'ordersSentDailyByShipper') {
                            promises.push(store.dispatch('dashboard/getDashboardItem', {
                                item: item,
                                paginate: '',
                                query: `?sent_date=${format(new Date(), 'YYYY-MM-DD')}`
                            }))
                        } else {
                            promises.push(store.dispatch('dashboard/getDashboardItem', {
                                item: item,
                                paginate: '',
                                query: ''
                            }))
                        }
                    })

                    Promise.all(promises)
                        .then(responses => {
                            responses.forEach(response => {
                                store.dispatch('dashboard/setDashboardItems', response.data)
                            })
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            next()
                        })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        }
    },
    beforeRouteLeave(to, from, next) {
        this.componentName = null
        store.dispatch('dashboard/clearDashboardFilters')
        next()
    },
    data() {
        return {
            carouselIndex: 0,
            fromReportsReportsShiftSummariesDetailedReports: false
        }
    },
    computed: {
        dashboardSections() {
            return this.$store.state.dashboard.dashboard.dashboardSections
        },
        hasMachinesDashboard() {
            return this.$store.state.dashboard.dashboard.hasMachinesDashboard
        },
        dashboardItems() {
            return this.$store.state.dashboard.dashboard.dashboardItems
        },
        userPermissions() {
            return this.$store.state.user.permissions
        },
        hasDashboardItems() {
            return this.dashboardItems.length > 0
        },
        productionDepartments() {
            return this.$store.state.administration.users.productionDepartments
        },
        savedFilters() {
            return this.$store.state.dashboard.dashboard.filters
        },
        machinesDashboard() {
            return this.$store.state.dashboard.dashboard.machinesDashboard
        },
    },
    methods: {
        getMachinesReviewDashboard() {
            if (!this.machinesDashboard.body) {
                store.dispatch('toggleMainLoader', true)
                this.getMachinesWidgets()
                    .then(() => this.carouselIndex++)
                    .finally(() => {
                        store.dispatch('toggleMainLoader', false)
                    })
            } else {
                this.carouselIndex++
            }
        },
        async getMachinesWidgets() {
            this.pagination.itemsPerPage = 8
            this.pagination.page = 1

            return await this.$store.dispatch('dashboard/getDashboardItem', {
                item: this.machinesDashboard,
                paginate: this.paginate(),
                query: '&showInDashboard=true'
            })
                .then(response => {
                    this.handlePaginationResponse(response.data.body)
                    store.dispatch('dashboard/savePagination', { pagination: this.pagination, paginationSettings: this.paginationSettings })
                })
                .catch(error => {
                    store.dispatch('toggleMainLoader', false)
                    errorHandler(error)
                })
        },
    }
}
</script>
